<template>
    <div>
        <div class="alert alert-danger mt-10 mb-30" role="alert"
             v-show="hasErrors()">
            <b>Please correct the following errors:</b>
            <ul>
                <li v-show="errors.fees_collection_date">{{ errors.fees_collection_date }}</li>
                <li v-show="errors.next_payment_date">{{ errors.next_payment_date }}</li>
                <li v-show="errors.payment_notes">{{ errors.payment_notes }}</li>
                <li v-for="error in errors.entries.fee_head_id">{{ error }}</li>
                <li v-for="error in errors.entries.due_for">{{ error }}</li>
                <li v-for="error in errors.entries.notes">{{ error }}</li>
                <li v-for="error in errors.entries.waived_amount">{{ error }}</li>
                <li v-for="error in errors.entries.paid_amount">{{ error }}</li>
                <li v-for="error in errors.entries.payment_method_id">{{ error }}</li>
                <li v-for="error in errors.entries.bank_account_id">{{ error }}</li>
                <li v-for="error in errors.entries.bank_transaction_id">{{ error }}</li>
                <li v-for="error in errors.entries.bank_transaction_date">{{ error }}</li>
                <li v-show="errors.total_amount">{{ errors.total_amount }}</li>
                <li v-for="error in errors.global_errors">{{ error }}</li>
            </ul>
        </div>

        <!-- Fees Collection Date -->
        <div id="collection-date" class="form-field-container col-md-8 col-lg-5 col-xxl-3">
            <FormElement :error="errors.fees_collection_date"
                         label="Date"
                         label-for-id="create_date"
                         label-class="required">
                <DatePicker :required="true" :extras="{ endDate: '0d' }"
                            :disabled="!hasEntryDateEditPermission"
                            :on-change="onFeeCollectionDateChanged"
                            :error="errors.fees_collection_date"/>
            </FormElement>
        </div>

        <!-- Total Paid Amount -->
        <div id="paid-amount-input-container" class="form-field-container col-md-8 col-lg-5 col-xxl-3 mt--15"
             v-show="feesCollectionPolicy === PRIORITIZED_PAYMENT">
            <FormElement :error="errors.total_amount"
                         label="Paid Amount"
                         label-for-id="total_paid_amount"
                         label-class="required">
                <input type="text"
                       title="Total Paid Amount"
                       id="total_paid_amount"
                       class="form-control"
                       ref="total_paid_amount"
                       v-bind:class="{ 'is-invalid' : errors.total_amount }"
                       v-model="totalPaidAmountInput"
                       @keyup="onTotalPaidAmountInputChanged"
                       @focusin="emptyInputOnZeroValue(null, 'total_paid_amount')"
                       @focusout="setZeroValueOnEmptyInput(null, 'total_paid_amount')"/>
            </FormElement>
        </div>

        <!-- Total Due Amount -->
        <div id="due-amount-display-container"
             class="form-field-container col-md-8 col-lg-5 col-xxl-3 mt--15 hidden-lg-up"
             v-show="feesCollectionPolicy === PRIORITIZED_PAYMENT">
            <FormElement label="Total Due">
                <div class="red-700">{{ currencySymbol }} {{ totalDueAmount().toLocaleString() }}</div>
            </FormElement>
        </div>

        <table id="fees-collections-desktop" class="fees-collections dataTable table table-striped hidden-md-down">
            <thead>
            <tr>
                <th class="text-left">Account</th>
                <th>For</th>
                <th>Notes</th>
                <th class="text-right">Actual</th>
                <th class="text-right">Payable</th>
                <th class="text-right">Waived</th>
                <th v-bind:class="{ 'text-right': feesCollectionPolicy === PRIORITIZED_PAYMENT }">Paid</th>
                <th v-show="shouldShowPaymentMethodChooser">Method</th>
                <th class="text-right">Due</th>
                <th>Action</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(entry, index) in entries" :key="index">
                <td class="text-left" v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <FeeHead :entry="entry"
                             :index="index"
                             :fee-heads="feeHeads"
                             :error="errors.entries.fee_head_id[index]"
                             :on-fee-head-change="onFeeHeadChange"
                             :show-due-from-receipt="true"/>
                </td>
                <td class="w-100" style="min-width: 100px"
                    v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <DueFor :ref="`due_for_desktop_${index}`"
                            :entry="entry"
                            :index="index"
                            :due-for-date-format="dueForDateFormat"
                            :on-due-for-change="onDueForChange"
                            :error="errors.entries.due_for[index]"/>
                </td>
                <td v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <Notes :entry="entries[index]"
                           :error="errors.entries.notes[index]"/>
                </td>
                <td class="w-100 text-right" v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <ActualAmount :entry="entry"/>
                </td>
                <td class="w-100 text-right" v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <PayableAmount :entry="entry"
                                   :index="index"
                                   :has-payable-edit-permission="hasPayableEditPermission"
                                   :error="errors.entries.amount[index]"/>
                </td>
                <td class="w-100" style="min-width: 100px"
                    v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <WaivedAmount :entry="entry"
                                  :index="index"
                                  :has-waiver-edit-permission="hasWaiverEditPermission"
                                  :error="errors.entries.waived_amount[index]"/>
                </td>
                <td class="w-150" style="min-width: 150px"
                    v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <div class="d-flex align-items-center">
                        <div class="w-100">
                            <PaidAmount :entry="entry"
                                        :fees-collection-policy="feesCollectionPolicy"
                                        :error="errors.entries.paid_amount[index]"/>
                        </div>
                        <div class="w-50">
                            <FillPaidAmountAction :entry="entry"
                                                  :index="index"
                                                  :should-populate-paid-amount="shouldPopulatePaidAmount"
                                                  :populate-paid-amount="populatePaidAmount"
                                                  :is-desktop-view="true"
                            />
                        </div>
                    </div>
                </td>
                <td v-show="shouldShowPaymentMethodChooser" style="min-width: 150px"
                    v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <PaymentMethod :entry="entry"
                                   :payment-methods="paymentMethods"
                                   :bank-accounts="bankAccounts"
                                   :payment-method-error="errors.entries.payment_method_id[index]"
                                   :bank-account-error="errors.entries.bank_account_id[index]"
                                   :bank-transaction-id-error="errors.entries.bank_transaction_id[index]"
                                   :bank-transaction-date-error="errors.entries.bank_transaction_date[index]"/>
                </td>
                <td class="w-100 text-right" v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <DueAmount :entry="entry"/>
                </td>
                <td class="w-50" v-bind:style="{ verticalAlign: tableCellVerticalAlign(index) }">
                    <ActionButtons :entry="entry"
                                   :index="index"
                                   :should-populate-paid-amount="shouldPopulatePaidAmount"
                                   :populate-paid-amount="populatePaidAmount"
                                   :remove-entry="removeEntry"/>
                </td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
                <th colspan="4" class="text-right">Total</th>
                <th class="text-right">{{ totalPayableAmount().toLocaleString() }}</th>
                <th class="text-right">{{ totalWaivedAmount().toLocaleString() }}</th>
                <th class="text-center">
                    <TotalAmount :amount="totalPaidAmount().toLocaleString()"
                                 :error="errors.total_amount"
                                 :show="showTotalAmount"/>
                </th>
                <th v-show="shouldShowPaymentMethodChooser"></th>
                <th class="text-right">{{ totalDueAmount().toLocaleString() }}</th>
                <th></th>
            </tr>
            </tfoot>
        </table>

        <table id="fees-collections-mobile" class="fees-collections dataTable table table-striped hidden-lg-up">
            <thead>
            <tr>
                <th class="text-center">Details</th>
                <th class="text-center">Payment</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(entry, index) in entries" :key="index">
                <td class="text-left">
                    <div class="font-size-18">
                        <FeeHead :entry="entry"
                                 :index="index"
                                 :fee-heads="feeHeads"
                                 :error="errors.entries.fee_head_id[index]"
                                 :on-fee-head-change="onFeeHeadChange"
                                 :show-due-from-receipt="false"/>
                    </div>
                    <div class="grey-600">
                        <DueFor :ref="`due_for_mobile_${index}`"
                                :entry="entry"
                                :index="index"
                                :due-for-date-format="dueForDateFormat"
                                :on-due-for-change="onDueForChange"
                                :error="errors.entries.due_for[index]"/>
                    </div>
                    <div class="d-flex font-size-16 mt-10">
                        {{ currencySymbol }} <PayableAmount :entry="entry"
                                         :index="index"
                                         :has-payable-edit-permission="hasPayableEditPermission"
                                         :error="errors.entries.amount[index]"/>
                    </div>
                    <div class="mt-5">
                        <NotesMobile :entry="entry"
                                     :error="errors.entries.notes[index]"/>
                    </div>
                </td>
                <td class="text-right" style="height: 1px">
                    <div class="d-flex flex-column justify-content-between h-p100">
                        <div>
                            <div class="d-flex justify-content-end"
                                 v-bind:class="{ 'font-size-16 green-700': feesCollectionPolicy === PRIORITIZED_PAYMENT }">
                                <div v-show="feesCollectionPolicy === PRIORITIZED_PAYMENT">
                                    <span class="mr-5">Paid: {{ currencySymbol }}</span>
                                </div>
                                <div class="d-flex align-items-center">
                                    <div style="flex: 1">
                                        <PaidAmount :entry="entry"
                                                    :fees-collection-policy="feesCollectionPolicy"
                                                    :error="errors.entries.paid_amount[index]"/>
                                    </div>
                                    <div class="w-40 ml-5">
                                        <FillPaidAmountAction :entry="entry"
                                                              :index="index"
                                                              :should-populate-paid-amount="shouldPopulatePaidAmount"
                                                              :populate-paid-amount="populatePaidAmount"
                                                              :is-desktop-view="false"
                                        />
                                    </div>
                                </div>
                            </div>
                            <PaymentMethod :entry="entry"
                                           :payment-methods="paymentMethods"
                                           :bank-accounts="bankAccounts"
                                           :should-show="shouldShowPaymentMethodChooser"
                                           :payment-method-error="errors.entries.payment_method_id[index]"
                                           :bank-account-error="errors.entries.bank_account_id[index]"
                                           :bank-transaction-id-error="errors.entries.bank_transaction_id[index]"
                                           :bank-transaction-date-error="errors.entries.bank_transaction_date[index]"/>
                            <div class="d-flex justify-content-end font-size-16 grey-600">
                                <div class="mr-5" style="white-space: nowrap">Waived: {{ currencySymbol }}</div>
                                <div>
                                    <WaivedAmount :entry="entry"
                                                  :index="index"
                                                  :has-waiver-edit-permission="hasWaiverEditPermission"
                                                  :error="errors.entries.waived_amount[index]"/>
                                </div>
                            </div>
                            <div class="d-flex justify-content-end font-size-20 font-weight-600 red-700">
                                <div class="mr-5" style="white-space: nowrap">Due: {{ currencySymbol }}</div>
                                <div>
                                    <DueAmount :entry="entry"/>
                                </div>
                            </div>
                        </div>
                        <div>
                            <ActionButtons :entry="entry"
                                           :index="index"
                                           :should-populate-paid-amount="this.shouldPopulatePaidAmount"
                                           :populate-paid-amount="populatePaidAmount"
                                           :remove-entry="removeEntry"/>
                        </div>
                    </div>
                </td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
                <th class="text-right">
                    <div class="d-flex justify-content-between">
                        <div>Total&nbsp;</div>
                        <div>Payable</div>
                    </div>
                    Waived<br/>
                    Paid<br/>
                    Due
                </th>
                <th class="text-right">
                    {{ currencySymbol }} {{ totalPayableAmount().toLocaleString() }}<br/>
                    {{ currencySymbol }} {{ totalWaivedAmount().toLocaleString() }}<br/>
                    <div class="d-flex justify-content-end w-p100">
                        <div>{{ currencySymbol }}&nbsp;</div>
                        <div>
                            <TotalAmount :amount="totalPaidAmount().toLocaleString()"
                                         :error="errors.total_amount"
                                         :show="showTotalAmount"/>
                        </div>
                    </div>
                    {{ currencySymbol }} {{ totalDueAmount().toLocaleString() }}
                </th>
            </tr>
            </tfoot>
        </table>

        <div class="mt-30">
            <!-- Add Fees -->
            <button id="add-fees-btn" type="button"
                    class="btn btn-outline btn-primary w-120 mb-20" @click="addEntry">
                <i class="fa fa-plus"></i>
                <span class="text ml-5">Add Fees</span>
            </button>

            <!-- Send SMS -->
            <div v-if="sendSmsEnabled" class="mb-30 mt-30">
                <div class="abc-checkbox abc-checkbox-circle abc-checkbox-success">
                    <input name="send_sms" id="send-sms" type="checkbox" v-model="sendSms"/>
                    <label for="send-sms" class="pl-20" style="padding-top: 8px">
                        Send SMS
                    </label>
                </div>
            </div>

            <!-- Send Email -->
            <div v-if="sendEmailEnabled" class="mb-30">
                <div class="abc-checkbox abc-checkbox-circle abc-checkbox-success">
                    <input name="send_email" id="send-email" type="checkbox" v-model="sendEmail"/>
                    <label for="send-email" class="pl-20" style="padding-top: 8px">
                        Send Email
                    </label>
                </div>
            </div>

            <!-- Bank Payment Slip -->
            <div v-if="bankPaySlipEnabled" class="mb-30">
              <form method="post" :action="bankPaySlipUrl">
                  <input type="hidden" name="_token" :value="csrfToken">
                  <input type="hidden" id="payslip-data" name="data"/>
                  <button type="submit" class="btn btn-primary btn-outline" @click="downloadBankPaymentSlip">
                      Bank Payment Slip
                  </button>
              </form>
            </div>

          <!-- Next Payment Date -->
          <div class="form-field-container col-md-8 col-lg-5 col-xxl-4 mb-20">
            <FormElement :error="errors.next_payment_date"
                         label="Next Payment Date:"
                         label-for-id="next_payment_date">
              <DatePicker :extras="{ startDate: '0d'}"
                          :value="nextPaymentDateValue()"
                          :on-change="(ref, date) => nextPaymentDate = date"
                          :error="errors.next_payment_date"/>
            </FormElement>
          </div>

          <!-- Next Payment Date -->
          <div class="form-field-container col-md-8 col-lg-5 col-xxl-4 mb-20">
            <FormElement :error="errors.payment_notes"
                         label="Payment Notes:"
                         label-for-id="payment_notes">
              <input class="form-control"
                     v-model="paymentNotes"
              >
            </FormElement>
          </div>

          <!-- Submit -->
          <button type="button" class="mt-5 ladda-button btn btn-primary w-120"
                  data-app-plugin="ladda" data-style="expand-left" id="collect-fees-submit-btn"
                  @click="confirmSubmission">
            Submit
          </button>
        </div>
    </div>
</template>

<script>
import {ref} from 'vue';
import FormElement from '../../components/FormElement';
import DatePicker from "../../components/DatePicker";
import FeeHead from "./components/FeeHead";
import DueFor from "./components/DueFor";
import Notes from "./components/Notes";
import ActualAmount from "./components/ActualAmount";
import PayableAmount from "./components/PayableAmount";
import WaivedAmount from "./components/WaivedAmount";
import PaidAmount from "./components/PaidAmount";
import PaymentMethod from "./components/PaymentMethod";
import DueAmount from "./components/DueAmount";
import ActionButtons from "./components/ActionButtons";
import TotalAmount from "./components/TotalAmount";
import NotesMobile from "./components/NotesMobile";
import FillPaidAmountAction from "./components/FillPaidAmountAction.vue";
import moment from "moment";

const PAYMENT_PER_ENTRY = 'payment_per_entry'
const PRIORITIZED_PAYMENT = 'prioritized_payment'
const RECURRENCE_PER_MONTH = 'per_month'
const RECURRENCE_PER_SESSION = 'per_session'

export default {
    components: {
        FeeHead,
        DueFor,
        Notes,
        ActualAmount,
        PayableAmount,
        WaivedAmount,
        PaidAmount,
        PaymentMethod,
        DueAmount,
        FillPaidAmountAction,
        ActionButtons,
        TotalAmount,
        NotesMobile,
        DatePicker,
        FormElement
    },
    props: {
        studentEnrollmentId: {
            type: Number,
            required: true,
        },
        nextPaymentDate: {
          type: String,
          required: false,
        },
        paymentNotes: {
          type: String,
          required: false,
        },
        entries: {
            type: Array,
            required: true,
        },
        feeHeads: {
            type: Array,
            required: true,
        },
        feeHeadDefaultAmounts: {
            type: Array,
            required: true,
        },
        paymentMethods: {
            type: Object,
            required: true,
        },
        cashPaymentMethodId: {
            type: Number,
            required: false,
            default: null,
        },
        bankAccounts: {
            type: Object,
            required: true,
        },
        waivers: {
            type: Object,
            required: true,
        },
        sendSmsEnabled: {
            type: Boolean,
            required: true,
        },
        sendEmailEnabled: {
            type: Boolean,
            required: true,
        },
        feesCollectionPolicy: {
            type: String,
            required: true,
        },
        hasWaiverEditPermission: {
            type: Boolean,
            required: true,
        },
        hasPayableEditPermission: {
            type: Boolean,
            required: true,
        },
        shouldPopulatePaidAmount: {
            type: Boolean,
            required: true,
        },
        feesCollectionStoreUrl: {
            type: String,
            required: true,
        },
        bankPaySlipEnabled: {
            type: String,
            required: false,
            default: false,
        },
        bankPaySlipUrl: {
            type: String,
            required: false,
            default: '',
        },
        latestReceiptId: {
            type: Number,
            required: false,
            default: null
        },
        currentSessionStartDate: {
            type: String,
            required: false,
            default: null
        },
        currentSessionEndDate: {
            type: String,
            required: false,
            default: null
        },
        hasEntryDateEditPermission: {
            type: Boolean,
            required: true,
        },
        currencySymbol: {
            type: String,
            required: true,
        }
    },
    setup(props) {
        return {
            feesCollectionDate: ref(null),
            nextPaymentDate: ref(props.nextPaymentDate ?? null),
            paymentNotes: ref(props.paymentNotes ?? null),
            entries: ref(props.entries),
            waiverInputIndexToShow: ref(null),
            sendSms: ref(props.sendSmsEnabled),
            sendEmail: ref(props.sendEmailEnabled),
            showTotalAmount: ref(true),
            errors: ref({
                entries: {
                    fee_head_id: {},
                    due_for: {},
                    notes: {},
                    amount: {},
                    waived_amount: {},
                    paid_amount: {},
                    payment_method_id: {},
                    bank_account_id: {},
                    bank_transaction_id: {},
                    bank_transaction_date: {},
                },
                total_amount: null,
                fees_collection_date: null,
                next_payment_date: null,
                payment_notes: null,
                global_errors: []
            }),
            totalPaidAmountInput: ref(0),
            currentSessionStartDate: ref(props.currentSessionStartDate),
            currentSessionEndDate: ref(props.currentSessionEndDate),
            csrfToken: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        }
    },
    computed: {
        shouldShowPaymentMethodChooser() {
            return Object.entries(this.paymentMethods).length > 1
                || (Object.entries(this.bankAccounts).length > 0
                    && Object.entries(Object.entries(this.bankAccounts)[0]).length > 1)
        },
        PAYMENT_PER_ENTRY: () => PAYMENT_PER_ENTRY,
        PRIORITIZED_PAYMENT: () => PRIORITIZED_PAYMENT,
        RECURRENCE_PER_MONTH: () => RECURRENCE_PER_MONTH,
        RECURRENCE_PER_SESSION: () => RECURRENCE_PER_SESSION,
    },
    methods: {
        moment(date) {
            moment.locale('en')
            return date ? moment(date) : moment()
        },
        nextPaymentDateValue() {
            if (!this.nextPaymentDate) {
                return null
            }

            const [day, month, year] = this.nextPaymentDate.split("-").map(Number)

            return new Date(year, month - 1, day)
        },
        hasErrors() {
            return Object.entries(this.errors.entries.fee_head_id).length > 0
                || Object.entries(this.errors.entries.due_for).length > 0
                || Object.entries(this.errors.entries.notes).length > 0
                || Object.entries(this.errors.entries.amount).length > 0
                || Object.entries(this.errors.entries.waived_amount).length > 0
                || Object.entries(this.errors.entries.paid_amount).length > 0
                || Object.entries(this.errors.entries.payment_method_id).length > 0
                || Object.entries(this.errors.entries.bank_account_id).length > 0
                || Object.entries(this.errors.entries.bank_transaction_id).length > 0
                || Object.entries(this.errors.entries.bank_transaction_date).length > 0
                || this.errors.total_amount
                || this.errors.fees_collection_date
                || this.errors.global_errors.length > 0
        },
        feeHeadDetails(id) {
            return this.feeHeads.find(head => id === head.id)
        },
        dueForDateFormat(feeHeadId) {
            switch (this.feeHeadDetails(feeHeadId)?.recurrence) {
                case RECURRENCE_PER_MONTH:
                    return 'MMM YYYY'
                case RECURRENCE_PER_SESSION:
                    return 'YYYY'
                default:
                    return 'DD-MM-YYYY'
            }
        },
        shouldShowPaidAmountInput(index) {
            const entry = this.entries[index]

            return (entry.amount - entry.waived_amount) > 0
        },
        emptyInputOnZeroValue(index, ref) {
            this.$nextTick(() => {
                const inputElem = this.$refs[index !== null ? `paid_amount_input_${index}` : ref]
                inputElem.value = inputElem.value > 0 ? Number(inputElem.value).toString() : ''
            })
        },
        setZeroValueOnEmptyInput(index, ref) {
            this.$nextTick(() => {
                const inputElem = this.$refs[index !== null ? `paid_amount_input_${index}` : ref]
                inputElem.value = inputElem.value !== 0 ? Number(inputElem.value).toString() : '0'
            })
        },
        tableCellVerticalAlign(index) {
            return this.bankAccounts[this.entries[index].payment_method_id] ? 'top !important' : 'middle'
        },
        dueAmount(index) {
            const entry = this.entries[index]

            return entry.amount - entry.waived_amount - (entry.paid_amount || 0)
        },
        totalPayableAmount() {
            return this.sum('amount')
        },
        totalWaivedAmount() {
            return this.sum('waived_amount')
        },
        totalPaidAmount() {
            return this.sum('paid_amount')
        },
        totalDueAmount() {
            return this.entries.map((item, index) => this.dueAmount(index)).reduce((a, b) => a + b, 0)
        },
        sum(property) {
            return this.entries.map(entry => Number(entry[property]) || 0).reduce((a, b) => a + b, 0)
        },
        onFeeCollectionDateChanged(ref, date) {
            this.feesCollectionDate = date
        },
        addEntry() {
            this.entries.push({
                id: null,
                due_from_receipt_id: null,
                due_from_receipt_display_id: null,
                fee_head_id: null,
                due_date: null,
                due_for: null,
                amount: 0,
                actual_amount: 0,
                notes: null,
                waived_amount: 0,
                paid_amount: 0,
                is_in_receipt: 0,
                payment_method_id: this.cashPaymentMethodId > 0
                    ? this.cashPaymentMethodId
                    : (Object.entries(this.paymentMethods).length > 0 ? Object.keys(this.paymentMethods)[0] : ''),
                bank_account_id: '',
                bank_transaction_id: null,
                bank_transaction_date: null,
                can_set_payable_on_fees_collection: false,
            })
            this.$nextTick(() => {
                $('#fees-collections-desktop .selectpicker').eq(this.entries.length - 1).selectpicker('refresh')
                $('#fees-collections-mobile .selectpicker').eq(this.entries.length - 1).selectpicker('refresh')
            })
        },
        onFeeHeadChange(index) {
            this.updateFeeAmount(index, false)
            this.updateDueForPicker(index)
        },
        updateFeeAmount(index, fromDueForChange) {
            const feeHeadAmount = Number(
                this.feeHeadDefaultAmounts.find(
                    headAmount => {
                        return headAmount.fee_head_id === this.entries[index].fee_head_id
                            && (
                                !headAmount.session
                                || !this.entries[index].due_for
                                || moment(this.entries[index].due_for).isBetween(
                                    moment(headAmount.session.start_date),
                                    moment(headAmount.session.end_date),
                                    'days',
                                    '[]'
                                )
                            );
                    }
                )?.amount
            ) || 0
            this.entries[index].amount = feeHeadAmount
            this.entries[index].actual_amount = feeHeadAmount
            if (this.shouldPopulatePaidAmount) {
                this.entries[index].paid_amount = feeHeadAmount - this.entries[index].waived_amount;
            }

            if (fromDueForChange === false) {
                this.entries[index].waived_amount = 0
                this.entries[index].due_for = null
            }

            const feeHead = this.feeHeads.find(head => head.id === this.entries[index].fee_head_id)
            this.entries[index].fee_head_priority = feeHead?.priority
            this.entries[index].can_set_payable_on_fees_collection = feeHead?.can_set_payable_on_fees_collection

            if (this.feesCollectionPolicy === PRIORITIZED_PAYMENT) {
                this.onTotalPaidAmountInputChanged()
            }
        },
        updateDueForPicker(index) {
            let format, viewMode

            switch (this.feeHeadDetails(this.entries[index].fee_head_id)?.recurrence) {
                case RECURRENCE_PER_MONTH:
                    format = 'M yyyy'
                    viewMode = 'months'
                    break;
                case RECURRENCE_PER_SESSION:
                    format = 'yyyy'
                    viewMode = 'years'
                    break;
                default:
                    format = 'dd-mm-yyyy'
                    viewMode = 'days'
            }

            this.entries[index].datepickerOptions = {
                pickerFormat: format,
                viewMode: viewMode
            }

            this.$nextTick(() => {
                this.$refs[`due_for_desktop_${index}`][0].forceUpdate()
                this.$refs[`due_for_mobile_${index}`][0].forceUpdate()
            })
        },
        onDueForChange(index) {
            this.entries[index].waived_amount = Number(this.waivers.find(
                waiver => waiver.fee_head_id === this.entries[index].fee_head_id
                    && this.entries[index].due_for >= waiver.start_date
                    && this.entries[index].due_for <= waiver.end_date
            )?.amount) || 0

            if (this.entries[index].due_for) {
                this.updateFeeAmount(index, true)
            }
        },
        onTotalPaidAmountInputChanged() {
            const prioritizedEntries = [...this.entries]
                .map((entry, index) => {
                    entry.index = index

                    return entry
                })
                .sort((a, b) => {
                    if (a.fee_head_priority > b.fee_head_priority) {
                        return 1
                    } else if (a.fee_head_priority < b.fee_head_priority) {
                        return -1
                    }

                    const dueForA = new Date(a.due_for)
                    dueForA.setHours(0, 0, 0, 0)
                    const dueForB = new Date(b.due_for)
                    dueForB.setHours(0, 0, 0, 0)

                    if (dueForA.getTime() > dueForB.getTime()) {
                        return 1
                    }

                    return -1
                })

            let remainingPaidAmount = this.totalPaidAmountInput
            prioritizedEntries.forEach(entry => {
                if (remainingPaidAmount <= 0) {
                    this.entries[entry.index].paid_amount = 0

                    return
                }

                const payableAmount = entry.amount - entry.waived_amount
                const assignablePaidAmount = remainingPaidAmount - payableAmount > 0
                    ? payableAmount
                    : remainingPaidAmount
                this.entries[entry.index].paid_amount = assignablePaidAmount
                remainingPaidAmount -= assignablePaidAmount
            });

            if (remainingPaidAmount > 0) {
                this.errors.total_amount = 'Total paid amount exceeds the sum of all payable entries.'
            } else {
                this.errors.total_amount = null
            }
        },
        populatePaidAmount(index, autoPopulatePaidAmount){
            if(autoPopulatePaidAmount){
                this.entries[index].paid_amount = this.entries[index].amount - this.entries[index].waived_amount;
            }
            else{
                this.entries[index].paid_amount = 0;
            }
        },
        removeEntry(index) {
            this.entries.splice(index, 1)
            this.entries.map((entry, index) => !entry.id ? this.onFeeHeadChange(index) : null)
            this.$nextTick(() => $('.selectpicker').selectpicker('refresh'))
        },
        downloadBankPaymentSlip() {
            $('#payslip-data').val(JSON.stringify(this.paymentSlipSubmissionData()))
        },
        async confirmSubmission() {
            if (this.currentSessionStartDate && this.currentSessionEndDate) {
                const currentSessionStartDate = new Date(this.currentSessionStartDate)
                const currentSessionEndDate = new Date(this.currentSessionEndDate)

                let hasDateBeyondCurrentSession = false
                this.entries.forEach(entry => {
                    const dueFor = new Date(entry.due_for)
                    if (dueFor < currentSessionStartDate || dueFor > currentSessionEndDate) {
                        hasDateBeyondCurrentSession = true
                    }
                })

                if (hasDateBeyondCurrentSession) {
                    const result = await Swal.fire(
                        {
                            title: 'Review fee head dates',
                            text: `Some fee head "For" dates are beyond the student's session. Are you sure the dates are correct?`,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: "#f2353c",
                            confirmButtonText: 'Yes, dates are correct',
                            cancelButtonText: 'Let me check the dates',
                        }
                    )
                    if (!result.value) {
                        window.Ladda.stopAll()
                        return
                    }
                }
            }

            this.showTotalAmount = false

            const {value: totalAmountConfirmation} = await Swal.fire(
                {
                    title: 'Amount Received',
                    input: 'text',
                    inputValue: '',
                    showCancelButton: true,
                    inputValidator: value => {
                        if (!value) {
                            return 'Please enter an amount.'
                        }

                        if (Number(value) !== this.sum('paid_amount')) {
                            return `The amount you entered here doesn\'t match with the amount you entered into the "Paid Amount" field.`
                        }
                    }
                }
            )

            if (totalAmountConfirmation) {
                this.errors = {
                    entries: {
                        fee_head_id: {},
                        due_for: {},
                        notes: {},
                        amount: {},
                        waived_amount: {},
                        paid_amount: {},
                        payment_method_id: {},
                        bank_account_id: {},
                        bank_transaction_id: {},
                        bank_transaction_date: {},
                    },
                    total_amount: null,
                    fees_collection_date: null,
                    global_errors: []
                }
                this.submit()
            } else {
                window.Ladda.stopAll()
            }

            this.showTotalAmount = true
        },
        submit() {
            fetch(this.feesCollectionStoreUrl,
                {
                    method: 'post',
                    headers: new Headers({
                        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/json'
                    }),
                    credentials: 'include',  // adds cookie header
                    body: JSON.stringify(this.submissionData())
                }
            ).then(async response => {
                if (response.status === 422) {
                    const json = await response.json()
                    this.showSubmissionErrors(this.transformSubmissionErrors(json.errors))
                    window.Ladda.stopAll()
                } else if (!response.ok) {
                    const error = await response.text()
                    toastr.error(`Failed collecting fees: ${error}`)
                    window.Ladda.stopAll()
                } else if (response.redirected) {
                    window.location.href = response.url
                }
            })
        },
        paymentSlipSubmissionData() {
          return {
            latest_receipt_id: this.latestReceiptId,
            fees_collection_date: this.feesCollectionDate,
            student_enrollment_id: this.studentEnrollmentId,
            entries: this.entries
          }
        },
        submissionData() {
            return {
                latest_receipt_id: this.latestReceiptId,
                fees_collection_date: this.feesCollectionDate,
                next_payment_date: this.nextPaymentDate,
                payment_notes: this.paymentNotes,
                student_enrollment_id: this.studentEnrollmentId,
                entries: this.entries,
                send_sms: this.sendSms,
                send_email: this.sendEmail,
            }
        },
        transformSubmissionErrors(serverSideErrors) {
            const errors = {
                entries: {
                    fee_head_id: {},
                    due_for: {},
                    notes: {},
                    amount: {},
                    waived_amount: {},
                    paid_amount: {},
                    payment_method_id: {},
                    bank_account_id: {},
                    bank_transaction_id: {},
                    bank_transaction_date: {},
                },
                total_amount: null,
                fees_collection_date: null,
                next_payment_date: null,
                payment_notes: null,
                global_errors: []
            }

            for (let prop in serverSideErrors) {
                if (prop.startsWith('entries')) {
                    const propParts = prop.split('.')
                    errors.entries[propParts[2]][propParts[1]] = serverSideErrors[prop][0]
                } else if (prop === 'fees_collection_date') {
                    errors.fees_collection_date = serverSideErrors[prop][0]
                } else if (prop === 'next_payment_date') {
                    errors.next_payment_date = serverSideErrors[prop][0]
                } else if (prop === 'payment_notes') {
                    errors.payment_notes = serverSideErrors[prop][0]
                } else if (prop === 'total_amount') {
                    errors.total_amount = serverSideErrors[prop][0]
                } else {
                    errors.global_errors.push(serverSideErrors[prop])
                }
            }

            return errors
        },
        showSubmissionErrors(errors) {
            console.log(errors)
            this.errors = errors
            toastr.error(`Please correct the errors specified.`)
            window.scrollTo({top: 0, behavior: 'smooth'})
        }
    },
}
</script>

<style>

#collection-date .form-control-input,
#paid-amount-input-container .form-control-input,
#due-amount-display-container .form-control-input {
    padding: 0;
}

.fees-collections tfoot th {
    font-weight: 600 !important;
    font-size: 20px;
}

@media (min-width: 768px) {
    .fees-collections th,
    .fees-collections td {
        text-align: center;
        vertical-align: middle !important;
    }
}

</style>
